import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { PageHeader } from "@/components/PageStates";
import { Button } from "@/components/ui/button";
import { wishes, child, formatDate } from "@/lib/mock-data";
import { useAppStore } from "@/lib/app-store";

export const Route = createFileRoute("/app/wishes/$id")({
  component: WishDetail,
});

function WishDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { role } = useAppStore();
  const wish = wishes.find((w) => w.id === id);

  if (!wish)
    return (
      <div>
        <PageHeader title="心愿" onBack={() => navigate({ to: "/app/wishes" })} />
        <p className="px-6 pt-10 text-center" style={{ color: "var(--muted)" }}>
          未找到该心愿
        </p>
      </div>
    );

  const canRedeem =
    (role === "admin" || role === "editor") && wish.status === "redeemable";
  const enough = child.availablePoints >= wish.required;

  return (
    <div className="pb-24">
      <PageHeader title="心愿详情" onBack={() => navigate({ to: "/app/wishes" })} />
      <div className="space-y-4 px-4 pt-4">
        <section className="rounded-[var(--r-lg)] border bg-surface p-4">
          <h2 className="text-[var(--fs-xl)] font-semibold">{wish.title}</h2>
          <div
            className="mt-1 text-[var(--fs-sm)]"
            style={{ color: "var(--muted)" }}
          >
            {wish.category}
            {wish.tags?.length ? ` · ${wish.tags.join(" ")}` : ""}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-y-3 text-[var(--fs-sm)]">
            <Field label="所需点数" value={`${wish.required} 分`} />
            <Field label="预算" value={wish.budget || "—"} />
            <Field label="约束" value={wish.constraints || "—"} />
            <Field label="预计时间" value={wish.expected || "—"} />
          </div>
          {wish.notes && (
            <p
              className="mt-3 rounded-[var(--r-sm)] p-2 text-[var(--fs-sm)]"
              style={{ background: "var(--surface-2)" }}
            >
              {wish.notes}
            </p>
          )}
        </section>

        <section className="rounded-[var(--r-lg)] border bg-surface p-4">
          <h3 className="mb-2 text-[var(--fs-sm)] font-semibold">详情</h3>
          <p className="whitespace-pre-line text-[var(--fs-sm)] leading-relaxed">
            {wish.detail}
          </p>
        </section>

        {wish.fulfillments && wish.fulfillments.length > 0 && (
          <section className="rounded-[var(--r-lg)] border bg-surface p-4">
            <h3 className="mb-2 text-[var(--fs-sm)] font-semibold">兑换记录</h3>
            <ul className="space-y-2">
              {wish.fulfillments.map((f, i) => (
                <li key={i} className="text-[var(--fs-sm)]">
                  {formatDate(f.at)} {f.note ? `· ${f.note}` : ""}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {canRedeem && (
        <div className="sticky bottom-0 border-t bg-surface/95 px-4 py-3 safe-bottom backdrop-blur">
          <Link
            to="/app/wishes/$id/redeem"
            params={{ id: wish.id }}
            aria-disabled={!enough}
          >
            <Button
              className="h-12 w-full text-base"
              disabled={!enough}
            >
              {enough
                ? "兑换心愿"
                : `还差 ${wish.required - child.availablePoints} 分`}
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p style={{ color: "var(--muted)" }}>{label}</p>
      <p className="mt-0.5">{value}</p>
    </div>
  );
}
